import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api'; 
import { useAuth } from '../contexts/AuthContext';
import SettleBlockModal from '../components/SettleBlockModal';

// ⬇️ [모바일 고정 해제 1/3] baseStyles.filtersContainer.position을 'relative'로 변경
const baseStyles = {
  filtersContainer: { 
    background: '#f8f9fa', 
    padding: '15px', 
    borderRadius: '5px',
    position: 'relative', // ⬅️ 'sticky'에서 'relative'로 변경 (모바일 기본값)
    top: 0, 
    zIndex: 10,
    borderBottom: '1px solid #dee2e6'
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  filterLabel: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: '#555',
    paddingLeft: '2px',
  },
  searchInput: { padding: '8px', fontSize: '0.9rem', width: '160px' },
  select: { padding: '8px', fontSize: '1rem', width: '160px' },
  searchButton: { padding: '8px 12px', fontSize: '0.9rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  
  // --- 검색 결과 테이블 ---
  resultsContainer: { marginTop: '20px' },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  countDisplay: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    paddingLeft: '5px' 
  },
  buttonGroup: { display: 'flex', gap: '10px' },
  excelButton: { padding: '8px 12px', fontSize: '0.9rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  settleButton: { padding: '8px 12px', fontSize: '0.9rem', backgroundColor: '#d9534f', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },

  table: { width: '100%', minWidth: '1500px', borderCollapse: 'collapse' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem', textAlign: 'center' },
  tdLeft: { textAlign: 'left', paddingLeft: '10px' },
  tdRight: { textAlign: 'right', paddingRight: '10px', fontFamily: 'monospace' },
  thCheckbox: { width: '40px' },
  tfoot: {
    background: '#f8f9fa',
    fontWeight: 'bold',
    borderTop: '2px solid #333',
  }
};


const LeaveSettleBatchPage = () => {
  const { user } = useAuth(); // 현재 로그인한 사용자 정보

  // --- 1. 상태(State) 정의 ---
  // ⬇️ [모바일 고정 해제 2/3] windowWidth state 추가
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    projectId: '',
    workplace: '',
    district: ''
  });
  const [projects, setProjects] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [settleLoading, setSettleLoading] = useState(false);
  const [settleMessage, setSettleMessage] = useState('');
  const [settleError, setSettleError] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [invalidParticipants, setInvalidParticipants] = useState([]);

  // --- 2. 동적 스타일 적용 (상단 고정) ---
  // ⬇️ [모바일 고정 해제] windowWidth useEffect 추가
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ⬇️ [모바일 고정 해제 3/3] 동적 styles 변수 (position 속성 추가)
  const styles = {
    ...baseStyles,
    filtersContainer: {
      ...baseStyles.filtersContainer,
      // 1024px 초과 (데스크탑)일 때만 'sticky'로 변경하고 top: 60px
      position: windowWidth > 1024 ? 'sticky' : 'relative',
      top: windowWidth > 1024 ? '60px' : '0px', 
    }
  };
  
  // --- 3. 사업 목록 불러오기 (연도 변경 시) --- (변경 없음)
  const fetchProjects = useCallback(async () => {
    if (!filters.year) return;
    try {
      const res = await api.get(`/projects?year=${filters.year}`);
      setProjects(res.data);
      setFilters(prev => ({ ...prev, projectId: '' })); 
    } catch (err) {
      console.error("사업 목록 로딩 실패", err);
      setProjects([]);
    }
  }, [filters.year]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // --- 4. 핸들러: 필터 변경 --- (변경 없음)
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // --- 5. 핸들러: 검색 (상단) --- (변경 없음)
  const handleSearch = useCallback(async () => {
    if (!filters.year) {
      setSearchError('연도는 필수입니다.');
      return;
    }
    setSearchLoading(true);
    setSearchError('');
    setSettleMessage('');
    setSettleError('');
    setSearchResults([]);
    setSelectedIds(new Set());
    
    try {
      const res = await api.get('/report/batch-search', { 
        params: filters 
      });
      setSearchResults(res.data);
    } catch (err) {
      setSearchError(err.response?.data?.message || '검색에 실패했습니다.');
    } finally {
      setSearchLoading(false);
    }
  }, [filters]);

  // --- 6. 핸들러: 체크박스 선택 (개별/전체) --- (변경 없음)
  const handleSelectOne = (id) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(id)) {
      newSelectedIds.delete(id);
    } else {
      newSelectedIds.add(id);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = searchResults.map(p => p.participantId);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  // --- 7. 핸들러: 엑셀 출력 --- (변경 없음)
  const handleExcelExport = async () => {
    setSearchError('');
    try {
      const res = await api.get('/settle/export', {
        params: filters,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `batch_settle_${filters.year}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSearchError(err.response?.data?.message || '엑셀 출력에 실패했습니다.');
    }
  };

  // --- 8. 핸들러: 일괄 정산 실행 --- (변경 없음)
  const handleBatchSettle = async () => {
    const idsToSettle = Array.from(selectedIds);
    if (idsToSettle.length === 0) {
      setSettleError('정산할 참여자를 선택하세요.');
      return;
    }

    // 참여 기간 검증
    try {
      const validationRes = await api.post('/settle/validate-batch-period', {
        participantIds: idsToSettle,
        year: filters.year
      });

      if (validationRes.data.hasInvalidData) {
        setInvalidParticipants(validationRes.data.invalidParticipants);
        setShowWarningModal(true);
        return; // 정산 진행 불가
      }
    } catch (err) {
      console.error('참여 기간 검증 실패:', err);
      alert('참여 기간 검증 중 오류가 발생했습니다. 정산을 진행할 수 없습니다.');
      return; // 검증 실패 시에도 정산 진행 불가
    }
    
    if (!window.confirm(`선택한 ${idsToSettle.length}명에 대해 미정산 연차를 정산하고, 참여 상태를 '종료'로 변경합니다. (참여종료일은 변경되지 않습니다)\n\n정말 실행하시겠습니까?`)) {
      return;
    }
    
    setSettleLoading(true);
    setSettleMessage('');
    setSettleError('');

    try {
      const res = await api.post('/settle/batch', {
        participantIds: idsToSettle,
        year: filters.year
      });
      setSettleMessage(res.data.message || `${res.data.successCount}명 정산 처리 완료.`);
      handleSearch(); 
    } catch (err) {
      const invalid = err.response?.data?.invalidParticipants;
      if (invalid?.length > 0) {
        setInvalidParticipants(invalid);
        setShowWarningModal(true);
        return;
      }
      setSettleError(err.response?.data?.message || '일괄 정산 처리에 실패했습니다.');
    } finally {
      setSettleLoading(false);
    }
  };

  // 렌더링용 변수 (변경 없음)
  const isAllSelected = searchResults.length > 0 && selectedIds.size === searchResults.length;
  const formatCurrency = (num) => `${(num || 0).toLocaleString()}원`;
  
  // 총계 계산 (변경 없음)
  const totalSettleAmount = searchResults.reduce((sum, p) => sum + (p.settleAmount || 0), 0);
  const totalRemainingUnpaidHours = searchResults.reduce((sum, p) => sum + (p.remainingUnpaidHours || 0), 0);

  // 경고 모달 스타일
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    },
    modal: {
      background: 'white',
      padding: '30px',
      borderRadius: '10px',
      width: '90%',
      maxWidth: '800px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
    },
    header: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#d9534f',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    content: {
      marginBottom: '20px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '15px'
    },
    th: {
      border: '1px solid #ddd',
      padding: '10px',
      background: '#f4f4f4',
      textAlign: 'left',
      fontSize: '0.9rem'
    },
    td: {
      border: '1px solid #ddd',
      padding: '8px',
      fontSize: '0.85rem'
    },
    closeButton: {
      padding: '10px 20px',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '1rem',
      marginTop: '20px',
      width: '100%'
    },
    warningText: {
      color: '#d9534f',
      fontWeight: 'bold',
      marginBottom: '10px'
    },
    infoBox: {
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '5px',
      padding: '15px',
      marginBottom: '20px',
      fontSize: '0.95rem',
      lineHeight: '1.6'
    }
  };

  // 생년월일 추출 — SettleBlockModal 내부에서 처리

  return (
    <div>
      <h2 className="no-print">🧾 연차 정산 (일괄)</h2>

      {/* 안내 메시지 */}
      <div className="no-print" style={modalStyles.infoBox}>
        <strong>⚠️ 중요 안내</strong><br />
        일괄정산은 참여종료일을 설정할 수 없으니, 사전에 <strong>참여자 관리</strong> 메뉴에서 종료일을 정확하게 입력한 후 정산하시기 바랍니다.
      </div>

      {/* 1. 검색 필터 (상단 고정) (변경 없음) */}
      <div style={styles.filtersContainer} className="no-print">
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>*연도</span>
            <input
              type="number"
              name="year"
              style={styles.select}
              value={filters.year}
              onChange={handleFilterChange}
              min="2020"
              step="1"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업명</span>
            <select name="projectId" style={styles.select} value={filters.projectId} onChange={handleFilterChange}>
              <option value="">-- 전체 사업명 --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.projectName}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>근무장소</span>
            <input
              type="text"
              name="workplace"
              style={styles.searchInput}
              value={filters.workplace}
              onChange={handleFilterChange}
              onKeyPress={handleFilterKeyPress}
              placeholder="근무장소 검색"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>읍면동</span>
            <input
              type="text"
              name="district"
              style={styles.searchInput}
              value={filters.district}
              onChange={handleFilterChange}
              onKeyPress={handleFilterKeyPress}
              placeholder="읍면동 검색"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>&nbsp;</span>
            <button style={styles.searchButton} onClick={handleSearch} disabled={searchLoading}>
              {searchLoading ? '검색중...' : '🔍 검색'}
            </button>
          </div>
        </div>
      </div>
      
      {/* 2. 검색 결과 테이블 (변경 없음) */}
      <div style={styles.resultsContainer} className="printable-area">
        <div style={styles.resultsHeader} className="no-print">
          <div style={styles.countDisplay}>
            {searchLoading ? '검색 중...' : `조회된 인원: 총 ${searchResults.length} 명 / 총 정산(예상)액: ${formatCurrency(totalSettleAmount)}`}
          </div>
          <div style={styles.buttonGroup}>
            <button 
              style={styles.excelButton} 
              onClick={handleExcelExport} 
              disabled={searchLoading || searchResults.length === 0}
            >
              📄 엑셀 출력
            </button>
            <button 
              style={styles.settleButton} 
              onClick={handleBatchSettle} 
              disabled={settleLoading || selectedIds.size === 0}
            >
              {settleLoading ? '처리 중...' : `⚠️ 일괄 정산 실행 (${selectedIds.size}명 선택)`}
            </button>
          </div>
        </div>
        
        {/* 메시지 표시 (변경 없음) */}
        {searchError && <p className="no-print" style={{ color: 'red' }}>{searchError}</p>}
        {settleMessage && <p className="no-print" style={{ color: 'green' }}>{settleMessage}</p>}
        {settleError && <p className="no-print" style={{ color: 'red' }}>{settleError}</p>}
        
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{...styles.th, ...styles.thCheckbox}} className="no-print">
                <input 
                  type="checkbox" 
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={searchLoading || searchResults.length === 0}
                />
              </th>
              <th style={styles.th}>사업명</th>
              <th style={styles.th}>성명</th>
              <th style={styles.th}>주민번호</th>
              <th style={styles.th}>참여시작일</th>
              <th style={styles.th}>참여종료일</th>
              <th style={styles.th}>은행</th>
              <th style={styles.th}>계좌번호</th>
              <th style={styles.th}>발생(A)</th>
              <th style={styles.th}>사용(B)</th>
              <th style={styles.th}>기지급(C)</th>
              <th style={styles.th}>미정산(A-B-C)</th>
              <th style={styles.th}>정산액(예상)</th>
              <th style={styles.th}>연차미발생월</th> 
            </tr>
          </thead>
          <tbody>
            {searchLoading ? (
              <tr><td colSpan="14" style={styles.td}>검색 중...</td></tr>
            ) : searchResults.length > 0 ? (
              searchResults.map(p => (
                <tr key={p.participantId}>
                  <td style={styles.td} className="no-print">
                    <input 
                      type="checkbox" 
                      checked={selectedIds.has(p.participantId)}
                      onChange={() => handleSelectOne(p.participantId)}
                    />
                  </td>
                  <td style={{...styles.td, ...styles.tdLeft}}>{p.projectName}</td>
                  <td style={styles.td}>{p.name}</td>
                  <td style={styles.td}>{p.rrn.substring(0, 8)}...</td>
                  <td style={styles.td}>{p.startDate}</td>
                  <td style={styles.td}>{p.endDate || '(진행중)'}</td>
                  <td style={styles.td}>{p.bankName || ''}</td>
                  <td style={styles.td}>{p.bankAccount || ''}</td>
                  <td style={styles.tdRight}>{p.totalGeneratedHours}h</td>
                  <td style={styles.tdRight}>{p.cumulativeUsedHours}h</td>
                  <td style={styles.tdRight}>{p.totalPaidOutHours}h</td>
                  <td style={{...styles.tdRight, fontWeight: 'bold', color: p.remainingUnpaidHours > 0 ? '#d9534f' : '#333'}}>
                    {p.remainingUnpaidHours}h
                  </td>
                  <td style={{...styles.tdRight, fontWeight: 'bold'}}>
                    {formatCurrency(p.settleAmount)}
                  </td>
                  <td style={{...styles.td, ...styles.tdLeft, fontSize: '0.8rem'}}>
                    {p.nonAccrualMonths}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="14" style={styles.td}> 
                  {searchError || '검색 결과가 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
          {/* 합계 행 ( ... 변경 없음 ... ) */}
          {!searchLoading && searchResults.length > 0 && (
            <tfoot style={styles.tfoot}>
              <tr>
                <td colSpan="11" style={{...styles.td, ...styles.tdRight, fontWeight: 'bold'}}>
                  합계
                </td>
                <td style={{...styles.td, ...styles.tdRight, color: totalRemainingUnpaidHours > 0 ? '#d9534f' : '#333'}}>
                  {totalRemainingUnpaidHours}h
                </td>
                <td style={{...styles.td, ...styles.tdRight}}>
                  {formatCurrency(totalSettleAmount)}
                </td>
                <td style={styles.td}></td> 
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showWarningModal && (
        <SettleBlockModal
          invalidParticipants={invalidParticipants}
          onClose={() => setShowWarningModal(false)}
        />
      )}
    </div>
  );
};

export default LeaveSettleBatchPage;